import { HttpStatus, StatusError } from '../types/status';
import { statusFromCode } from '../utils/status';

const httpErrorSymbol = Symbol('http-error');

export class HttpError extends Error implements StatusError {
	public readonly __entity = httpErrorSymbol;

	constructor(
		public readonly status: number,
		public readonly redirectURL?: string
	) {
		super(`HTTP error status: ${statusFromCode(status)}`);
	}

	toString() {
		return statusFromCode(this.status);
	}

	get isClientError() {
		return this.status >= 400 && this.status < 500;
	}

	get isServerError() {
		return this.status >= 500 && this.status < 600;
	}

	get isBadRequest() {
		return this.status === HttpStatus.BadRequest;
	}

	get isUnauthorized() {
		return this.status === HttpStatus.Unauthorized;
	}

	get isForbidden() {
		return this.status === HttpStatus.Forbidden;
	}

	get isNotFound() {
		return this.status === HttpStatus.NotFound;
	}

	get isConflict() {
		return this.status === HttpStatus.Conflict;
	}
}

export const isHttpError = (object: unknown): object is HttpError => {
	return !!object && typeof object === 'object' && '__entity' in object && object.__entity === httpErrorSymbol;
};

type HttpErrorResponse = {
	response: {
		status: number;
	};
};

const isHttpErrorResponse = (err: unknown): err is HttpErrorResponse => {
	return (
		!!err &&
		typeof err === 'object' &&
		'response' in err &&
		!!err.response &&
		typeof err.response === 'object' &&
		'status' in err.response &&
		typeof err.response.status === 'number'
	);
};

export const toHttpError = (err: unknown): HttpError => {
	if (isHttpError(err)) {
		return err;
	}

	if (isHttpErrorResponse(err)) {
		return new HttpError(err.response.status);
	}

	return new HttpError(500);
};
